// Iter36 — UI language picker (Level 1).
//
// Compact dropdown showing 🇬🇧/🇩🇪/🇭🇺. Persists to localStorage and, when the
// user is signed in, syncs the choice to user.locale via PATCH /api/users/me.
// Works on the login screen too (skips the API sync when no user is present).
import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { LOCALE_META, SUPPORTED_LOCALES, useT } from "../lib/i18n";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const LanguageSelector = ({
  variant = "pill", // 'pill' | 'menu-row' | 'compact'
  align = "right",
  hasUser = true,
  testIdPrefix = "language-selector",
}) => {
  const { locale, setLocale } = useT();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const choose = async (next) => {
    if (next === locale) {
      setOpen(false);
      return;
    }
    setLocale(next);
    setOpen(false);
    if (hasUser) {
      try {
        await axios.patch(`${API}/users/me`, { locale: next });
      } catch (_) {
        // Non-fatal — local change already applied.
      }
    }
  };

  const meta = LOCALE_META[locale] || LOCALE_META.en;

  return (
    <div className="relative" ref={ref} data-testid={`${testIdPrefix}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 px-2 py-1 rounded-md transition-colors hover:bg-white/[0.06]"
        style={{
          background: "transparent",
          border: "1px solid var(--border-strong, rgba(255,255,255,0.14))",
          color: "var(--text, #f0f2f8)",
          fontFamily: "DM Mono, monospace",
          fontSize: 11,
          letterSpacing: "0.5px",
          cursor: "pointer",
          lineHeight: 1,
        }}
        aria-label="Change language"
        data-testid={`${testIdPrefix}-trigger`}
        data-current-locale={locale}
      >
        <span aria-hidden style={{ fontSize: 13 }}>{meta.flag}</span>
        <span>{meta.short}</span>
        <span aria-hidden style={{ opacity: 0.55, marginLeft: 2 }}>▾</span>
      </button>
      {open && (
        <div
          className="absolute mt-1.5 rounded-md overflow-hidden z-[400]"
          style={{
            [align === "right" ? "right" : "left"]: 0,
            width: 160,
            background: "var(--bg-surface, #0d0f18)",
            border: "1px solid var(--border-strong, rgba(255,255,255,0.14))",
            boxShadow: "0 8px 24px rgba(0,0,0,0.35)",
          }}
          role="menu"
          data-testid={`${testIdPrefix}-menu`}
        >
          {SUPPORTED_LOCALES.map((lc) => {
            const m = LOCALE_META[lc];
            const isActive = lc === locale;
            return (
              <button
                key={lc}
                onClick={() => choose(lc)}
                className="w-full flex items-center gap-2 px-3 py-2 transition-colors"
                style={{
                  background: isActive ? "rgba(24,95,165,0.18)" : "transparent",
                  border: "none",
                  color: isActive ? "var(--blue, #9ec3ec)" : "var(--text, #f0f2f8)",
                  fontFamily: "DM Mono, monospace",
                  fontSize: 12,
                  letterSpacing: "0.4px",
                  cursor: "pointer",
                  textAlign: "left",
                }}
                onMouseEnter={(e) => {
                  if (!isActive) e.currentTarget.style.background = "rgba(127,127,127,0.10)";
                }}
                onMouseLeave={(e) => {
                  if (!isActive) e.currentTarget.style.background = "transparent";
                }}
                data-testid={`${testIdPrefix}-option-${lc}`}
                role="menuitemradio"
                aria-checked={isActive}
              >
                <span aria-hidden style={{ fontSize: 14 }}>{m.flag}</span>
                <span className="flex-1">{m.name}</span>
                {isActive && <span style={{ color: "#9ec3ec" }}>✓</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Inline variant for Settings page — renders 3 large pill buttons side by side.
export const LanguageInlinePicker = ({ hasUser = true, testIdPrefix = "language-inline" }) => {
  const { locale, setLocale } = useT();

  const choose = async (next) => {
    if (next === locale) return;
    setLocale(next);
    if (hasUser) {
      try {
        await axios.patch(`${API}/users/me`, { locale: next });
      } catch (_) {}
    }
  };

  return (
    <div className="flex flex-wrap gap-2" data-testid={testIdPrefix}>
      {SUPPORTED_LOCALES.map((lc) => {
        const m = LOCALE_META[lc];
        const isActive = lc === locale;
        return (
          <button
            key={lc}
            onClick={() => choose(lc)}
            className="flex items-center gap-2 px-3 py-2 rounded-md transition-all"
            style={{
              background: isActive ? "rgba(24,95,165,0.18)" : "rgba(255,255,255,0.04)",
              border: `1px solid ${isActive ? "rgba(24,95,165,0.7)" : "rgba(255,255,255,0.10)"}`,
              color: isActive ? "#9ec3ec" : "#f0f2f8",
              fontFamily: "DM Mono, monospace",
              fontSize: 12,
              letterSpacing: "0.4px",
              cursor: "pointer",
              minWidth: 110,
            }}
            data-testid={`${testIdPrefix}-${lc}`}
            aria-pressed={isActive}
          >
            <span aria-hidden style={{ fontSize: 16 }}>{m.flag}</span>
            <span className="flex-1 text-left">{m.name}</span>
            {isActive && <span style={{ color: "#9ec3ec" }}>✓</span>}
          </button>
        );
      })}
    </div>
  );
};

// Display-language (Level 2) picker. Same UX shape but a 4th option: Original.
export const DisplayLanguagePicker = ({ hasUser = true, testIdPrefix = "display-language" }) => {
  const { displayLocale, setDisplayLocale, t } = useT();

  const choose = async (next) => {
    if (next === displayLocale) return;
    setDisplayLocale(next);
    if (hasUser) {
      try {
        await axios.patch(`${API}/users/me`, { display_locale: next });
      } catch (_) {}
    }
  };

  const options = [
    { value: "original", flag: "🌐", name: t("settings.content_language_original") },
    { value: "en", flag: LOCALE_META.en.flag, name: LOCALE_META.en.name },
    { value: "de", flag: LOCALE_META.de.flag, name: LOCALE_META.de.name },
    { value: "hu", flag: LOCALE_META.hu.flag, name: LOCALE_META.hu.name },
  ];

  return (
    <div className="flex flex-wrap gap-2" data-testid={testIdPrefix}>
      {options.map((o) => {
        const isActive = o.value === displayLocale;
        return (
          <button
            key={o.value}
            onClick={() => choose(o.value)}
            className="flex items-center gap-2 px-3 py-2 rounded-md transition-all"
            style={{
              background: isActive ? "rgba(155,89,182,0.18)" : "rgba(255,255,255,0.04)",
              border: `1px solid ${isActive ? "rgba(155,89,182,0.6)" : "rgba(255,255,255,0.10)"}`,
              color: isActive ? "#c89adb" : "#f0f2f8",
              fontFamily: "DM Mono, monospace",
              fontSize: 12,
              letterSpacing: "0.4px",
              cursor: "pointer",
              minWidth: 120,
            }}
            data-testid={`${testIdPrefix}-${o.value}`}
            aria-pressed={isActive}
          >
            <span aria-hidden style={{ fontSize: 16 }}>{o.flag}</span>
            <span className="flex-1 text-left">{o.name}</span>
            {isActive && <span style={{ color: "#c89adb" }}>✓</span>}
          </button>
        );
      })}
    </div>
  );
};
